import { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api/axiosInstance';
import { toast } from 'react-toastify';
import { useRazorpay } from "react-razorpay";
import { loadRazorpayCheckout, CHECKOUT_UNAVAILABLE_MESSAGE } from "../utils/razorpayCheckout";
import subscriptionPurchaseService from '../services/subscriptionPurchaseService';
import { isYearlyPlan, crossedOutPrice } from '../utils/planPricing';
import '../assets/css/PurchasePlan.css';
import { planHighlights, planTier } from '../utils/planFeatures';
import { isCurrentPlan as matchesCurrentPlan, plansWithCurrent, periodLabel } from '../utils/currentPlan';

// The backend's "already subscribed" answer (error.invalid, 400). A generic database
// error (code 601) used to stand in for it while error.invalid was missing; it is
// a real failure now and is shown as such.
export const isAlreadySubscribedError = (data) =>
  /already have an active subscription/i.test((data && (data.message || data.error?.message)) || '');

// Activating a paid first payment is retried this many times, this far apart
export const ACTIVATION_ATTEMPTS = 3;
export const ACTIVATION_RETRY_MS = 3000;

/**
 * Posts a payment verification. When the backend says a paid first payment
 * "could not be activated yet", it has released the order for a retry: the
 * same request is sent again (the seller is never asked to pay twice).
 */
export const postWithActivationRetry = async (endpoint, payload, retryable, attempt = 1) => {
  try {
    return await api.post(endpoint, payload);
  } catch (err) {
    const released = /could not be activated yet/i.test(err.response?.data?.message || '');
    if (retryable && released && attempt < ACTIVATION_ATTEMPTS) {
      await new Promise(resolve => setTimeout(resolve, ACTIVATION_RETRY_MS));
      return postWithActivationRetry(endpoint, payload, retryable, attempt + 1);
    }
    throw err;
  }
};

const API_BASE_URL = process.env.REACT_APP_APIURL;

// ═══════════════════════════════════════════════════════════════
// COUNTDOWN TIMER COMPONENT (for discount offers)
// ═══════════════════════════════════════════════════════════════
const CountdownTimer = ({ endDate }) => {
    const [timeLeft, setTimeLeft] = useState('');
    const [urgency, setUrgency] = useState('low');
    
    useEffect(() => {
        const updateTimer = () => {
            const now = new Date();
            const end = new Date(endDate);
            const diff = end - now;
            
            if (diff <= 0) {
                setTimeLeft('Expired');
                setUrgency('expired');
                return;
            }
            
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            
            // Set urgency level
            const hoursRemaining = diff / (1000 * 60 * 60);
            if (hoursRemaining < 1) setUrgency('critical');
            else if (hoursRemaining < 24) setUrgency('high');
            else setUrgency('medium');
            
            if (days > 0) {
                setTimeLeft(`${days}d ${hours}h`);
            } else if (hours > 0) {
                setTimeLeft(`${hours}h ${minutes}m`);
            } else {
                setTimeLeft(`${minutes}m`);
            }
        };
        
        updateTimer();
        const interval = setInterval(updateTimer, 60000); // Update every minute
        return () => clearInterval(interval);
    }, [endDate]);
    
    // Same look as the /purchase-plan countdown (PurchasePlan.css .pp-count)
    const isUrgent = urgency === 'critical' || urgency === 'high' || urgency === 'expired';
    return (
        <span className={`pp-count${isUrgent ? ' urgent' : ''}`}>
            Offer ends in <strong>{timeLeft}</strong>
        </span>
    );
};

const Subscription = () => {
  const { Razorpay } = useRazorpay();
  const [loading, setLoading] = useState(true);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [businessUserId, setBusinessUserId] = useState('');
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [availablePlans, setAvailablePlans] = useState([]);
  // Only annual plans are offered (Plan Management's yearly plans with Auto-Renew on)
  const yearlyPlans = availablePlans.filter(isYearlyPlan);
  // Plus the business's own plan when it is no longer offered (e.g. legacy monthly)
  const shownPlans = plansWithCurrent(yearlyPlans, subscriptionInfo);
  
  // Pagination state for payment history
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Validation modal state
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [pendingPlanId, setPendingPlanId] = useState(null);
  const [pendingPlanName, setPendingPlanName] = useState(null);
  const [pendingDiscountId, setPendingDiscountId] = useState(null);

  // Fetch data on mount
  useEffect(() => {
    const initializeSubscription = async () => {
      // Get business user ID from localStorage
      const userId = localStorage.getItem('business_id');
      
      console.log('Initializing subscription with userId:', userId);
      
      if (userId) {
        setBusinessUserId(userId);
        
        // First fetch subscription info
        await fetchSubscriptionInfo(userId);
        
        // If subscription exists, sync status with Razorpay
        const tempSubInfo = await api.get('/subscription/info', {
          params: { business_user_id: userId }
        }).catch(() => null);
        
        if (tempSubInfo?.data?.result?.has_subscription && tempSubInfo?.data?.result?.subscription_id) {
          // Sync status from Razorpay to ensure database is up to date
          await syncSubscriptionStatus(userId, tempSubInfo.data.result.subscription_id);
          // Fetch again after sync
          await fetchSubscriptionInfo(userId);
        }
        
        await fetchPaymentHistory(userId);
        
        // Fetch available subscription plans with discount for this user
        await fetchAvailablePlans(userId);
      } else {
        console.warn('No business_id found in localStorage');
        setLoading(false);
        
        // Still fetch plans without discount
        await fetchAvailablePlans(null);
      }
      
      // Get current plan ID from localStorage
      const planId = localStorage.getItem('current_plan_id');
      if (planId) {
        setCurrentPlanId(planId);
      }
    };

    initializeSubscription();
  }, []);

  // Fetch subscription info
  const fetchSubscriptionInfo = async (userId) => {
    try {
      setLoadingSubscription(true);
      const response = await api.get('/subscription/info', {
        params: { business_user_id: userId }
      });
      
      console.log('Subscription info response:', response.data);
      
      if (response.data.status === 'success') {
        const data = response.data.result;
        
        if (data.has_subscription) {
          if (data.is_cancelled) {
            // Cancelled subscription - show cancelled state
            console.log('Subscription is cancelled:', data);
            setSubscriptionInfo({
              ...data,
              isCancelled: true,
              status: 'cancelled'
            });
          } else {
            // Active subscription
            console.log('Setting active subscription info:', data);
            setSubscriptionInfo({
              ...data,
              isCancelled: false
            });
          }
        } else {
          // No subscription
          console.log('No subscription found');
          setSubscriptionInfo(null);
        }
      }
      setLoading(false);
      setLoadingSubscription(false);
    } catch (err) {
      console.error('Error fetching subscription:', err);
      console.error('Error details:', err.response?.data);
      setLoading(false);
      setLoadingSubscription(false);
    }
  };

  // Fetch payment history
  const fetchPaymentHistory = async (userId) => {
    try {
      setLoadingHistory(true);
      const response = await api.get('/subscription/payment-history', {
        params: { business_user_id: userId }
      });
      
      console.log('Payment history response:', response.data);
      
      // Backend returns 'result' not 'data'
      if (response.data.status === 'success' && response.data.result && response.data.result.payments) {
        setPaymentHistory(response.data.result.payments);
      }
      setLoadingHistory(false);
    } catch (err) {
      console.error('Error fetching payment history:', err);
      console.error('Error details:', err.response?.data);
      setLoadingHistory(false);
    }
  };

  // Fetch available subscription plans from database (WITH DISCOUNTS)
  const fetchAvailablePlans = async (userId) => {
    try {
      setLoadingPlans(true);
      
      // Use new discount-aware endpoint if business_user_id is available
      // Endpoint: /subscription-plans/list-with-discounts (not /subscription/plans/)
      const endpoint = userId 
        ? `/subscription-plans/list-with-discounts?business_user_id=${userId}`
        : '/subscription/plans/list';
      
      console.log('Fetching subscription plans from:', endpoint);
      const response = await api.get(endpoint);
      
      console.log('Available subscription plans response:', response.data);
      
      if (response.data.status === 'success') {
        // Handle both old and new response formats
        const plans = response.data.result?.plans || response.data.data?.plans || [];
        console.log('Fetched subscription plans:', plans);
        setAvailablePlans(plans);
      } else {
        // Fallback to empty array if no plans found
        setAvailablePlans([]);
      }
      setLoadingPlans(false);
    } catch (err) {
      console.error('Error fetching available plans:', err);
      console.error('Error details:', err.response?.data);
      
      // If discount endpoint fails (404), fallback to regular endpoint
      if (err.response?.status === 404 && userId) {
        console.log('Discount endpoint not found, falling back to regular endpoint');
        try {
          const fallbackResponse = await api.get('/subscription/plans/list');
          if (fallbackResponse.data.status === 'success') {
            const plans = fallbackResponse.data.data?.plans || fallbackResponse.data.result?.plans || [];
            setAvailablePlans(plans);
          }
        } catch (fallbackErr) {
          console.error('Fallback also failed:', fallbackErr);
          setAvailablePlans([]);
        }
      } else {
        // Fallback to empty array on error
        setAvailablePlans([]);
      }
      setLoadingPlans(false);
    }
  };

  // Sync subscription status from Razorpay to database
  const syncSubscriptionStatus = async (userId, subscriptionId) => {
    try {
      console.log('Syncing subscription status:', subscriptionId);
      
      const response = await api.post('/subscription/sync', {
        business_user_id: userId,
        subscription_id: subscriptionId
      });

      console.log('Sync response:', response.data);

      if (response.data.status === 'success') {
        const result = response.data.result;
        console.log(`Subscription synced: ${result.old_status} → ${result.new_status}`);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error syncing subscription:', err);
      console.error('Error details:', err.response?.data);
      return false;
    }
  };

  // Create subscription (WITH DISCOUNT SUPPORT)
  const handleSubscribe = async (planId, planName, discountId = null) => {
    if (!businessUserId) {
      toast.error('Please login to subscribe');
      return;
    }

    // Validate subscription purchase
    try {
      console.log('Validating subscription purchase for plan:', planId);
      
      // Get current product and domain counts
      const productCount = await subscriptionPurchaseService.getCurrentProductCount(businessUserId);
      const domainCount = await subscriptionPurchaseService.getCurrentDomainCount(businessUserId);
      
      // Validate purchase
      const validation = await subscriptionPurchaseService.validatePurchase(
        planId,
        planName,
        subscriptionInfo,
        productCount,
        domainCount
      );
      
      console.log('Validation result:', validation);
      
      // If blocked, show error and stop
      if (!validation.allowed) {
        const formatted = subscriptionPurchaseService.formatValidationMessages(validation);
        
        // Show blockers
        let errorMessage = formatted.description + '\n\n';
        formatted.blockers.forEach(blocker => {
          errorMessage += blocker + '\n';
        });
        
        toast.error(errorMessage, { autoClose: 8000 });
        return;
      }
      
      // If confirmation required, show modal
      if (validation.confirmationRequired) {
        setPendingPlanId(planId);
        setPendingPlanName(planName);
        setPendingDiscountId(discountId);
        setValidationResult(validation);
        setShowValidationModal(true);
        return;
      }
      
      // If no confirmation needed (new subscription), proceed directly
      await proceedWithSubscription(planId, discountId);
      
    } catch (error) {
      console.error('Validation error:', error);
      toast.error('Failed to validate subscription. Please try again.');
    }
  };

  // Proceed with subscription after validation (WITH DISCOUNT SUPPORT - HYBRID APPROACH)
  const proceedWithSubscription = async (planId, discountId = null) => {
    setLoading(true);
    // Wording for the Razorpay checkout ("Yearly Subscription")
    const billingPeriod = (availablePlans.find(p => p.plan_id === planId || p.razorpay_plan_id === planId) || {}).billing_period;
    setShowValidationModal(false);

    // Razorpay's script first: no subscription for a checkout that can't open
    if (!(await loadRazorpayCheckout())) {
      toast.error(CHECKOUT_UNAVAILABLE_MESSAGE, { toastId: 'checkout-unavailable' });
      setLoading(false);
      return;
    }

    try {
      console.log('Creating subscription for plan:', planId, 'with discount:', discountId);
      
      // Debug: Log the discount information
      console.log('💰 Subscription discount info:', {
        planId,
        discountId,
        hasDiscount: !!discountId
      });
      
      // Use hybrid approach if discount is available
      const endpoint = discountId ? '/subscription/create-with-discount' : '/subscription/create';
      const payload = {
        business_user_id: businessUserId,
        plan_id: planId
      };
      
      // Add discount_id if available
      if (discountId) {
        payload.discount_id = discountId;
      }
      
      console.log('📤 Sending request to:', endpoint, 'with payload:', payload);
      
      const response = await api.post(endpoint, payload);


      if (response.data.status !== 'success') {
        // Check for duplicate subscription error
        if (isAlreadySubscribedError(response.data)) {
          
          // Refresh subscription info to show current state
          await fetchSubscriptionInfo(businessUserId);
          
          toast.error('You already have an active subscription. Please cancel your current subscription before creating a new one.', {
            autoClose: 8000
          });
          setLoading(false);
          return;
        }
        throw new Error(response.data.message || response.data.error?.message);
      }

      // Backend returns 'result' or 'data'
      const result = response.data.result || response.data.data;
      const { subscription_id, order_id, razorpay_key } = result;
      
      // Store the plan_id in localStorage to track which plan user subscribed to
      localStorage.setItem('current_plan_id', planId);
       
      // Validate required fields
      if ((!subscription_id && !order_id) || !razorpay_key) {
        throw new Error('Invalid subscription response from server');
      }

      console.log('Opening Razorpay with:', { subscription_id, order_id });

      // Check if Razorpay is loaded
      if (!Razorpay) {
        toast.error('Payment system is loading. Please try again.');
        setLoading(false);
        return;
      }

      // Open Razorpay Checkout
      const options = {
        key: razorpay_key, // Must match the backend Razorpay key
        currency: 'INR',
        name: 'MyTamilKart',
        description: discountId
          ? `First ${periodLabel(billingPeriod)} Payment (Discounted)`
          : `${periodLabel(billingPeriod)} Subscription`,
        
        handler: async function (response) {
          console.log('Payment successful:', response);
          // Call backend to verify payment
          await verifySubscription(response, discountId);
        },
        prefill: {
          name: localStorage.getItem('business_name') || '',
          email: localStorage.getItem('business_email') || '',
          contact: localStorage.getItem('business_phone') || ''
        },
        theme: {
          color: '#1E5B3A',
          backdrop_color: 'rgba(0, 0, 0, 0.6)'
        },
        modal: {
          ondismiss: function() {
            console.log('Payment modal dismissed');
            setLoading(false);
            toast.info('Payment cancelled');
          },
          escape: true,
          confirm_close: true,
          animation: true,
          backdropclose: false,
          handleback: true
        },
        config: {
          display: {
            blocks: {
              banks: {
                name: 'All payment methods',
                instruments: [
                  { method: 'upi' },
                  { method: 'card' },
                  { method: 'netbanking' },
                  { method: 'wallet' }
                ]
              }
            },
            sequence: ['block.banks'],
            preferences: {
              show_default_blocks: true
            }
          }
        }
      };
      
      // Add subscription_id or order_id based on response
      if (subscription_id) {
        options.subscription_id = subscription_id;
      } else if (order_id) {
        options.order_id = order_id;
        options.amount = result.amount ? Math.round(parseFloat(result.amount) * 100) : undefined;
      }


      const rzp = new Razorpay(options);
      
      rzp.on('payment.failed', function (response) {
        console.error('Payment failed:', response.error);
        toast.error(`Payment failed: ${response.error.description || 'Please try again'}`);
        setLoading(false);
      });

      // Open Razorpay
      rzp.open();
    } catch (err) {
      console.error('Error creating subscription:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      let errorMessage = 'Failed to create subscription. Please try again.';
      
      // Check for duplicate subscription error
      if (isAlreadySubscribedError(err.response?.data)) {
        
        // Refresh subscription info to show current state
        await fetchSubscriptionInfo(businessUserId);
        
        errorMessage = 'You already have an active subscription. Please cancel your current subscription before creating a new one.';
      } else if (err.response?.status === 503) {
        errorMessage = 'Service temporarily unavailable. Please try again in a moment.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast.error(errorMessage, { autoClose: 8000 });
      setLoading(false);
    }
  };

  // Verify subscription (WITH DISCOUNT SUPPORT)
  const verifySubscription = async (razorpayResponse, hasDiscount = false) => {
    try {
      console.log('Verifying subscription:', razorpayResponse);
      
      // Use new verification endpoint if discount was applied
      const endpoint = hasDiscount ? '/subscription/verify-and-activate' : '/subscription/verify';
      
      const payload = {
        business_user_id: businessUserId,
        razorpay_subscription_id: razorpayResponse.razorpay_subscription_id,
        razorpay_payment_id: razorpayResponse.razorpay_payment_id,
        razorpay_signature: razorpayResponse.razorpay_signature,
        razorpay_order_id: razorpayResponse.razorpay_order_id
      };
      // If activating an already-paid first payment fails, the backend releases it
      // for a retry: try again with the same payment, never ask the seller to pay again
      const response = await postWithActivationRetry(endpoint, payload, hasDiscount);

      console.log('Verification response:', response.data);

      if (response.data.status === 'success') {
        // Backend returns 'result' or 'data'
        const result = response.data.result || response.data.data || {};
        const nextBillingDate = result.next_billing_date;
        const subscriptionId = razorpayResponse.razorpay_subscription_id || result.subscription_id;
        const discountSaved = result.discount_saved;
        
        // Show success message with discount info if applicable
        if (hasDiscount && discountSaved) {
          toast.success(`🎉 Subscription activated with discount! You saved ₹${discountSaved}. Next billing: ${nextBillingDate || 'at your next renewal'} at regular price.`, {
            autoClose: 10000
          });
        } else if (nextBillingDate) {
          toast.success(`🎉 Subscription activated successfully! Next billing: ${nextBillingDate}`);
        } else {
          toast.success('🎉 Subscription activated successfully!');
        }
        
        // Smoothly refresh subscription data without page reload
        setLoadingSubscription(true);
        setLoadingHistory(true);
        setLoadingPlans(true);
        
        setTimeout(async () => {
          try {
            // Force sync from Razorpay to get latest status
            if (subscriptionId) {
              await syncSubscriptionStatus(businessUserId, subscriptionId);
            }
            
            // Fetch updated subscription info and payment history
            await fetchSubscriptionInfo(businessUserId);
            await fetchPaymentHistory(businessUserId);
            
            // Update current plan ID from localStorage
            const planId = localStorage.getItem('current_plan_id');
            if (planId) {
              setCurrentPlanId(planId);
            }
            
            setLoading(false);
            setLoadingSubscription(false);
            setLoadingHistory(false);
            setLoadingPlans(false);
          } catch (error) {
            console.error('Error refreshing subscription data:', error);
            setLoading(false);
            setLoadingSubscription(false);
            setLoadingHistory(false);
            setLoadingPlans(false);
            // If smooth refresh fails, do a hard reload as fallback
            toast.info('Refreshing page to show updated subscription...');
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }
        }, 2000);
      } else {
        throw new Error(response.data.message || 'Subscription verification failed');
      }
    } catch (err) {
      console.error('Subscription verification failed:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Verification failed. Please contact support.';
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  // Cancel subscription
  const handleCancelSubscription = async () => {
    // Check if subscription is already cancelled before showing confirmation
    if (subscriptionInfo.status === 'cancelled' || subscriptionInfo.isCancelled) {
      toast.info('This subscription is already cancelled');
      return;
    }

    if (!window.confirm('Are you sure you want to cancel your subscription? You will receive a confirmation email.')) return;

    setLoading(true);

    try {
      const response = await api.post('/subscription/cancel', {
        business_user_id: businessUserId,
        subscription_id: subscriptionInfo.subscription_id
      });

      if (response.data.status === 'success') {
        toast.success('🎉 Subscription cancelled successfully! Check your email for confirmation.');
        
        // Clear the current plan from localStorage since it's cancelled
        localStorage.removeItem('current_plan_id');
        
        // Smoothly refresh subscription data without page reload
        setLoadingSubscription(true);
        setLoadingHistory(true);
        setLoadingPlans(true);
        
        setTimeout(async () => {
          try {
            // Force sync from Razorpay to get latest cancelled status
            await syncSubscriptionStatus(businessUserId, subscriptionInfo.subscription_id);
            
            // Fetch updated subscription info and payment history
            await fetchSubscriptionInfo(businessUserId);
            await fetchPaymentHistory(businessUserId);
            
            // Clear current plan since it's cancelled
            setCurrentPlanId(null);
            
            setLoading(false);
            setLoadingSubscription(false);
            setLoadingHistory(false);
            setLoadingPlans(false);
          } catch (error) {
            console.error('Error refreshing subscription data:', error);
            setLoading(false);
            setLoadingSubscription(false);
            setLoadingHistory(false);
            setLoadingPlans(false);
            // If smooth refresh fails, do a hard reload as fallback
            toast.info('Refreshing page to show updated subscription...');
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }
        }, 2000);
      } else {
        throw new Error(response.data.message || 'Failed to cancel subscription');
      }
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      console.error('Error response:', err.response?.data);
      
      // Check if it's already cancelled
      if (err.response?.data?.message?.includes('cancelled status') || 
          err.response?.data?.message?.includes('not cancellable') ||
          err.response?.data?.message?.includes('already cancelled')) {
        toast.info('This subscription is already cancelled');
        
        // Immediately update state to cancelled
        setSubscriptionInfo({
          ...subscriptionInfo,
          status: 'cancelled',
          isCancelled: true
        });
        
        // Clear current plan
        localStorage.removeItem('current_plan_id');
        setCurrentPlanId(null);
        
        // Fetch fresh data to sync with backend
        setTimeout(async () => {
          try {
            await fetchSubscriptionInfo(businessUserId);
            await fetchPaymentHistory(businessUserId);
          } catch (error) {
            console.error('Error fetching updated data:', error);
          }
        }, 500);
      } else {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to cancel subscription';
        toast.error(errorMessage);
      }
      
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString || dateString === null || dateString === 'null') {
      return 'Not available';
    }
    
    const date = new Date(dateString);
    
    // Check if date is valid (allow dates from 1970 onwards for billing periods)
    if (isNaN(date.getTime())) {
      return 'Not available';
    }
    
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format date with time
  const formatDateTime = (dateString) => {
    if (!dateString || dateString === null || dateString === 'null') {
      return 'Not available';
    }
    
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Not available';
    }
    
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get status badge style
  const getStatusBadgeStyle = (status) => {
    const statusMap = {
      'active': { background: '#2E7D4F', color: 'white' },
      'authenticated': { background: '#2E7D4F', color: 'white' },
      'created': { background: '#ffc107', color: '#212529' },
      'pending': { background: '#ffc107', color: '#212529' },
      'halted': { background: '#ff6b6b', color: 'white' },
      'cancelled': { background: '#5B5040', color: 'white' },
      'expired': { background: '#343a40', color: 'white' }
    };
    return statusMap[status] || { background: '#5B5040', color: 'white' };
  };

  // Show loading screen
  if (loading) {
    return (
      <>
        <main>
          <div style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #FDF9F2 0%, #e9ecef 100%)'
        }}>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 30px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #1E5B3A 0%, #2E7D4F 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 10px 30px rgba(7, 101, 52, 0.3)',
              animation: 'pulse 2s ease-in-out infinite'
            }}>
              <i className="fa-solid fa-spinner fa-spin" style={{
                fontSize: '2.5em',
                color: 'white'
              }}></i>
            </div>
            <h2 style={{
              color: '#1E5B3A',
              fontSize: '1.8em',
              fontWeight: '700',
              marginBottom: '15px'
            }}>
              Loading Subscription...
            </h2>
            <p style={{
              color: '#5B5040',
              fontSize: '1.1em'
            }}>
              Please wait while we fetch your subscription details
            </p>
          </div>
          <style>{`
            @keyframes pulse {
              0%, 100% {
                transform: scale(1);
                box-shadow: 0 10px 30px rgba(7, 101, 52, 0.3);
              }
              50% {
                transform: scale(1.05);
                box-shadow: 0 15px 40px rgba(7, 101, 52, 0.4);
              }
            }
            
            /* Responsive Styles for Subscription Page */
            @media (max-width: 768px) {
              .subscription-header h1 {
                font-size: 1.8em !important;
              }
              .subscription-header p {
                font-size: 1em !important;
              }
              .subscription-card {
                padding: 20px !important;
                margin-bottom: 20px !important;
              }
              .subscription-card h2 {
                font-size: 1.5em !important;
              }
              .subscription-detail-card {
                padding: 15px !important;
                margin-bottom: 15px !important;
              }
              .subscription-detail-card i {
                font-size: 1.2em !important;
              }
              .subscription-detail-card strong {
                font-size: 1em !important;
              }
              .subscription-detail-card p {
                font-size: 0.9em !important;
              }
              .status-badge {
                font-size: 1em !important;
                padding: 10px 25px !important;
              }
              .cancel-button {
                padding: 12px 30px !important;
                font-size: 1em !important;
              }
              .payment-history-table {
                font-size: 0.85em !important;
              }
              .payment-history-table th,
              .payment-history-table td {
                padding: 10px 8px !important;
              }
              .plan-card {
                margin-bottom: 20px !important;
              }
              .back-button {
                top: 10px !important;
                left: 10px !important;
                width: 40px !important;
                height: 40px !important;
              }
            }
            
            @media (max-width: 576px) {
              .subscription-header {
                padding-top: 60px !important;
                padding-bottom: 30px !important;
              }
              .subscription-section {
                padding-top: 30px !important;
                padding-bottom: 30px !important;
              }
              .subscription-card {
                padding: 15px !important;
              }
              .subscription-card h2 {
                font-size: 1.3em !important;
              }
              .subscription-detail-card {
                padding: 12px !important;
              }
              .status-badge {
                font-size: 0.9em !important;
                padding: 8px 20px !important;
              }
              .payment-summary-card {
                padding: 15px !important;
              }
              .payment-summary-card > div:first-child {
                font-size: 0.8em !important;
              }
              .payment-summary-card > div:last-child {
                font-size: 1.5em !important;
              }
            }
          `}</style>
        </div>
      </main>
      </>
    );
  }

  // Always render the main subscription page for all users (with or without subscription)
  // This ensures consistent UI for everyone, including new signups
  if (true) {
    const statusStyle = subscriptionInfo ? getStatusBadgeStyle(subscriptionInfo.status) : { background: '#5B5040', color: 'white' };
    
    // Check if payment has been made (includes discount first payments)
    const hasActivePaidSubscription = subscriptionInfo && 
      !subscriptionInfo.isCancelled &&
      subscriptionInfo.status !== 'cancelled' &&
      (subscriptionInfo.paid_count > 0 || subscriptionInfo.first_payment_discount_amount > 0);
    
    // Check if subscription is cancelled
    const isCancelledSubscription = subscriptionInfo && 
      (subscriptionInfo.isCancelled || subscriptionInfo.status === 'cancelled');
    
    return (
      <>
        <main className="mtk-sub-page">
        {/* Header and plans: the /purchase-plan design (assets/css/PurchasePlan.css) */}
        <div className="mtk-pp mtk-pp-sub">
          <div className="pp-w">
            <div className="pp-top">
              <button
                type="button"
                className="pp-back"
                aria-label="Back"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.history.back();
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
              </button>
            </div>

            <section className="ph-hero" aria-label="Auto-Renew">
              <span className="k">தானியங்கி புதுப்பித்தல் · Auto&#8209;Renew</span>
              <span className="billnote">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><path d="M21 12a9 9 0 1 1-3-6.7L21 8" /><path d="M21 3v5h-5" /></svg>
                Renews automatically · Prices + GST
              </span>
            </section>

            {loadingPlans ? (
              // Placeholder cards while the plans load
              <section className="plans2" aria-label="Plans" aria-busy="true">
                {[0, 1, 2].map(i => (
                  <article key={i} className={`pc${i === 1 ? ' pop' : ''}`}>
                    <span className="pp-skel" style={{ width: '40%', height: '28px' }} />
                    <span className="pp-skel" style={{ width: '60%', height: '40px' }} />
                    <span className="pp-skel" style={{ width: '100%', height: '52px', borderRadius: '99px' }} />
                  </article>
                ))}
              </section>
            ) : shownPlans.length === 0 ? (
              <div className="pp-empty" role="status">
                Subscription plans are being configured. Please check back later.
              </div>
            ) : (
              <section className="plans2" aria-label={hasActivePaidSubscription ? 'Available plans' : 'Choose your plan'}>
                {shownPlans.map((plan) => {
                  // Determine if this is the middle plan for popular badge
                  const isPopular = !plan.is_current_only && yearlyPlans.indexOf(plan) === Math.floor(yearlyPlans.length / 2);

                  // Convert limits array to features array for display
                  const ownFeatures = plan.features && plan.features.length > 0
                    ? plan.features
                    : plan.limits
                      ? plan.limits.map(limit => `${limit.label}: ${limit.value}`)
                      : [];
                  // Without its own list, the plan's highlights from the /purchase-plan comparison
                  const planFeatures = ownFeatures.length > 0
                    ? ownFeatures
                    : plan.is_current_only ? [] : planHighlights(planTier(plan));

                  return (
                    <PlanCard
                      key={plan.razorpay_plan_id || plan.plan_id}
                      planId={plan.razorpay_plan_id || plan.plan_id}
                      name={plan.plan_name}
                      price={(plan.price || plan.original_price || 0).toLocaleString('en-IN')}
                      originalPrice={plan.original_price}
                      yearlyPrice={plan.price || plan.original_price}
                      description={plan.description}
                      features={planFeatures}
                      isPopular={isPopular}
                      isCurrent={matchesCurrentPlan(plan, subscriptionInfo, currentPlanId)}
                      onSubscribe={handleSubscribe}
                      loading={loading}
                      businessUserId={businessUserId}
                      isPaid={hasActivePaidSubscription}
                      billingPeriod={plan.billing_period}
                      hasDiscount={plan.has_discount}
                      discount={plan.discount}
                      subscriptionInfo={subscriptionInfo}
                    />
                  );
                })}
              </section>
            )}
          </div>
        </div>

        {/* Plans, Subscription Info, and Payment History */}
        <section className="subscription-section" style={{ backgroundColor: '#FAF3E7', paddingTop: '24px', paddingBottom: '50px' }}>
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-xl-10 col-lg-10 col-md-12">

                {/* 2a. Cancelled Subscription Info - Show if cancelled */}
                {isCancelledSubscription &&  (
                  <div className="subscription-card" style={{
                    background: 'linear-gradient(135deg, #fff4f0 0%, #ffe8e0 100%)',
                    borderRadius: '20px',
                    padding: '40px',
                    boxShadow: '0 8px 30px rgba(255, 107, 107, 0.15)',
                    border: '2px solid #ff9999',
                    marginBottom: '40px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: '-50px',
                      right: '-50px',
                      width: '150px',
                      height: '150px',
                      background: 'linear-gradient(135deg, #ff9999 0%, #ff8080 100%)',
                      borderRadius: '50%',
                      opacity: '0.1'
                    }}></div>

                    <h2 style={{ 
                      fontSize: '2em', 
                      color: '#ff6b6b', 
                      marginBottom: '30px',
                      fontWeight: '700',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '15px'
                    }}>
                      <i className="fa-solid fa-ban" style={{ color: '#ff6b6b' }}></i>
                      Cancelled Subscription
                    </h2>

                    <div style={{ 
                      textAlign: 'center',
                      marginBottom: '30px',
                      padding: '20px',
                      background: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
                    }}>
                      <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                        Status
                      </p>
                      <span className="status-badge" style={{
                        background: '#ff8080',
                        color: 'white',
                        padding: '15px 40px',
                        borderRadius: '30px',
                        fontSize: '1.3em',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        letterSpacing: '1.5px',
                        display: 'inline-block',
                        boxShadow: '0 4px 15px rgba(220, 53, 69, 0.3)'
                      }}>
                        CANCELLED
                      </span>
                    </div>

                    <div className="row" style={{ marginBottom: '20px' }}>
                      {subscriptionInfo.cancelled_date && (
                        <div className="col-md-6 mb-4">
                          <div className="subscription-detail-card" style={{
                            background: 'white',
                            padding: '20px',
                            borderRadius: '12px',
                            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
                            height: '100%'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                              <i className="fa-solid fa-calendar-xmark" style={{ fontSize: '1.5em', color: '#ff8080' }}></i>
                              <strong style={{ fontSize: '1.1em', color: '#333' }}>Cancelled Date</strong>
                            </div>
                            <p style={{ fontSize: '1.1em', color: '#ff6b6b', marginBottom: '0', fontWeight: '600' }}>
                              {formatDate(subscriptionInfo.cancelled_date)}
                            </p>
                          </div>
                        </div>
                      )}

                      {subscriptionInfo.paid_count > 0 && (
                        <div className="col-md-6 mb-4">
                          <div className="subscription-detail-card" style={{
                            background: 'white',
                            padding: '20px',
                            borderRadius: '12px',
                            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
                            height: '100%'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                              <i className="fa-solid fa-check-circle" style={{ fontSize: '1.5em', color: '#2E7D4F' }}></i>
                              <strong style={{ fontSize: '1.1em', color: '#333' }}>Total Payments Made</strong>
                            </div>
                            <p style={{ fontSize: '2em', color: '#2E7D4F', marginBottom: '0', fontWeight: '700' }}>
                              {subscriptionInfo.paid_count}
                            </p>
                          </div>
                        </div>
                      )}

                      {subscriptionInfo.subscription_id && (
                        <div className="col-md-12 mb-4">
                          <div className="subscription-detail-card" style={{
                            background: 'white',
                            padding: '20px',
                            borderRadius: '12px',
                            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
                            height: '100%'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                              <i className="fa-solid fa-id-card" style={{ fontSize: '1.5em', color: '#5B5040' }}></i>
                              <strong style={{ fontSize: '1.1em', color: '#333' }}>Subscription ID</strong>
                            </div>
                            <p style={{ fontSize: '0.95em', color: '#666', marginBottom: '0', wordBreak: 'break-all' }}>
                              {subscriptionInfo.subscription_id}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div style={{ 
                      marginTop: '20px', 
                      textAlign: 'center',
                      padding: '20px',
                      background: 'white',
                      borderRadius: '12px'
                    }}>
                      <p style={{ fontSize: '1.1em', color: '#333', marginBottom: '15px' }}>
                        Ready to continue? Subscribe to a new plan below!
                      </p>
                      <button
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        style={{
                          padding: '15px 40px',
                          fontSize: '1.1em',
                          fontWeight: '600',
                          color: 'white',
                          background: 'linear-gradient(135deg, #2E7D4F 0%, #2E7D4F 100%)',
                          border: 'none',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 4px 15px rgba(0, 200, 83, 0.3)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '10px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 200, 83, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 200, 83, 0.3)';
                        }}
                      >
                        <i className="fa-solid fa-rocket"></i>
                        Subscribe to New Plan
                      </button>
                    </div>
                  </div>
                )}

                {/* 2b. Current Subscription Status - Show only if payment has been made and not cancelled */}
                {loadingSubscription ? (
                  <div style={{
                    background: 'white',
                    borderRadius: '20px',
                    padding: '40px',
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                    border: '2px solid #e0e0e0',
                    marginBottom: '40px',
                    textAlign: 'center'
                  }}>
                    <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '3em', color: '#1E5B3A', marginBottom: '20px' }}></i>
                    <p style={{ fontSize: '1.2em', color: '#666', marginBottom: '0' }}>Loading Subscription Status...</p>
                  </div>
                ) : hasActivePaidSubscription && (
                  <>
                    {/* Current Subscription Status - Prominent Display */}
                    <div className="subscription-card" style={{
                      background: 'linear-gradient(135deg, #ffffff 0%, #FDF9F2 100%)',
                      borderRadius: '20px',
                      padding: '40px',
                      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                      border: '2px solid #1E5B3A',
                      marginBottom: '40px',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      {/* Decorative Corner */}
                      <div style={{
                        position: 'absolute',
                        top: '-50px',
                        right: '-50px',
                        width: '150px',
                        height: '150px',
                        background: 'linear-gradient(135deg, #1E5B3A 0%, #2E7D4F 100%)',
                        borderRadius: '50%',
                        opacity: '0.1'
                      }}></div>

                      <h2 style={{ 
                        fontSize: '2em', 
                        color: '#1E5B3A', 
                        marginBottom: '30px',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '15px'
                      }}>
                        <i className="fa-solid fa-crown" style={{ color: '#D9640E' }}></i>
                        Current Subscription Status
                      </h2>

                      {/* Status Badge - Large and Prominent */}
                      <div style={{ 
                        textAlign: 'center',
                        marginBottom: '30px',
                        padding: '20px',
                        background: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
                      }}>
                        <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                          Subscription Status
                        </p>
                        <span className="status-badge" style={{
                          ...statusStyle,
                          padding: '15px 40px',
                          borderRadius: '30px',
                          fontSize: '1.3em',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          letterSpacing: '1.5px',
                          display: 'inline-block',
                          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
                        }}>
                          {subscriptionInfo.status}
                        </span>
                      </div>

                      {/* Subscription Details Grid */}
                      <div className="row" style={{ marginBottom: '20px' }}>
                        {/* Subscription ID */}
                        <div className="col-md-6 mb-4">
                          <div className="subscription-detail-card" style={{
                            background: 'white',
                            padding: '20px',
                            borderRadius: '12px',
                            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
                            height: '100%'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                              <i className="fa-solid fa-id-card" style={{ fontSize: '1.5em', color: '#1E5B3A' }}></i>
                              <strong style={{ fontSize: '1.1em', color: '#333' }}>Subscription ID</strong>
                            </div>
                            <p style={{ fontSize: '0.95em', color: '#666', marginBottom: '0', wordBreak: 'break-all' }}>
                              {subscriptionInfo.subscription_id}
                            </p>
                          </div>
                        </div>

                        {/* Next Billing Date */}
                        <div className="col-md-6 mb-4">
                          <div className="subscription-detail-card" style={{
                            background: 'white',
                            padding: '20px',
                            borderRadius: '12px',
                            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
                            height: '100%'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                              <i className="fa-solid fa-calendar-days" style={{ fontSize: '1.5em', color: '#1E5B3A' }}></i>
                              <strong style={{ fontSize: '1.1em', color: '#333' }}>Next Billing Date</strong>
                            </div>
                            <p style={{ fontSize: '1.1em', color: '#1E5B3A', marginBottom: '0', fontWeight: '600' }}>
                              {formatDate(subscriptionInfo.next_billing_date)}
                            </p>
                          </div>
                        </div>

                        {/* Payments Made */}
                        <div className="col-md-6 mb-4">
                          <div className="subscription-detail-card" style={{
                            background: 'white',
                            padding: '20px',
                            borderRadius: '12px',
                            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
                            height: '100%'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                              <i className="fa-solid fa-check-circle" style={{ fontSize: '1.5em', color: '#2E7D4F' }}></i>
                              <strong style={{ fontSize: '1.1em', color: '#333' }}>Payments Made</strong>
                            </div>
                            <p style={{ fontSize: '2em', color: '#2E7D4F', marginBottom: '0', fontWeight: '700' }}>
                              {subscriptionInfo.paid_count}
                            </p>
                          </div>
                        </div>

                        {/* Remaining Payments */}
                        <div className="col-md-6 mb-4">
                          <div className="subscription-detail-card" style={{
                            background: 'white',
                            padding: '20px',
                            borderRadius: '12px',
                            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
                            height: '100%'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                              <i className="fa-solid fa-clock" style={{ fontSize: '1.5em', color: '#ffc107' }}></i>
                              <strong style={{ fontSize: '1.1em', color: '#333' }}>Remaining Payments</strong>
                            </div>
                            <p style={{ fontSize: '2em', color: '#ffc107', marginBottom: '0', fontWeight: '700' }}>
                              {subscriptionInfo.remaining_count}
                            </p>
                          </div>
                        </div>
                      </div>
                  
                      {/* Cancel Button - Show if user has made payment (check paid_count OR first_payment exists) */}
                      {subscriptionInfo.status && 
                       (subscriptionInfo.paid_count > 0 || subscriptionInfo.first_payment_discount_amount > 0) && 
                       subscriptionInfo.status !== 'cancelled' && (
                        <div style={{ marginTop: '20px', textAlign: 'center' }}>
                          <button 
                            className="cancel-button"
                            onClick={handleCancelSubscription} 
                            disabled={loading}
                            style={{
                              padding: '15px 40px',
                              fontSize: '1.1em',
                              fontWeight: '600',
                              color: 'white',
                              background: '#ff8080',
                              border: 'none',
                              borderRadius: '10px',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              boxShadow: '0 4px 15px rgba(220, 53, 69, 0.3)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '10px'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#ff6b6b';
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 107, 107, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#ff8080';
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 128, 128, 0.3)';
                            }}
                          >
                            <i className="fa-solid fa-ban"></i>
                            {loading ? 'Processing...' : 'Cancel Subscription'}
                          </button>
                        </div>
                      )}
                      
                      {/* Cancelled Subscription Message */}
                      {(subscriptionInfo.status === 'cancelled' || subscriptionInfo.isCancelled) && (
                        <div style={{ 
                          marginTop: '20px', 
                          textAlign: 'center',
                          padding: '25px',
                          background: 'linear-gradient(135deg, #fff4f0 0%, #ffe8e0 100%)',
                          borderRadius: '12px',
                          border: '2px solid #ffcccc',
                          boxShadow: '0 4px 15px rgba(255, 107, 107, 0.1)'
                        }}>
                          <i className="fa-solid fa-ban" style={{ fontSize: '3em', color: '#ff8080', marginBottom: '15px' }}></i>
                          <p style={{ 
                            fontSize: '1.3em', 
                            color: '#721c24', 
                            marginBottom: '10px',
                            fontWeight: '700'
                          }}>
                            Subscription Cancelled
                          </p>
                          {subscriptionInfo.cancelled_date && (
                            <p style={{ fontSize: '1em', color: '#721c24', marginBottom: '10px' }}>
                              Cancelled on: <strong>{formatDate(subscriptionInfo.cancelled_date)}</strong>
                            </p>
                          )}
                          {subscriptionInfo.paid_count > 0 && (
                            <p style={{ fontSize: '0.95em', color: '#856404', marginBottom: '15px', background: '#fff3cd', padding: '10px', borderRadius: '8px' }}>
                              Total payments made: <strong>{subscriptionInfo.paid_count}</strong>
                            </p>
                          )}
                          <p style={{ fontSize: '0.95em', color: '#721c24', marginBottom: '15px' }}>
                            Your subscription has been cancelled. You can subscribe to a new plan anytime below.
                          </p>
                          <button
                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                            style={{
                              padding: '12px 30px',
                              fontSize: '1em',
                              fontWeight: '600',
                              color: 'white',
                              background: 'linear-gradient(135deg, #2E7D4F 0%, #2E7D4F 100%)',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              boxShadow: '0 4px 15px rgba(0, 200, 83, 0.3)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '10px'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 200, 83, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 200, 83, 0.3)';
                            }}
                          >
                            <i className="fa-solid fa-rocket"></i>
                            Subscribe Again
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* 3. Payment History - Show if there are payments */}
                {paymentHistory.length > 0 && (
                <div style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '40px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  border: '1px solid #e0e0e0',
                  marginTop: '40px'
                }}>
                  <h2 style={{ 
                    fontSize: '1.8em', 
                    color: '#1E5B3A', 
                    marginBottom: '25px',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <i className="fa-solid fa-receipt" style={{ color: '#D9640E' }}></i>
                    Payment History
                  </h2>
                  
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ 
                      width: '100%', 
                      borderCollapse: 'collapse',
                      fontSize: '0.95em'
                    }}>
                      <thead>
                        <tr style={{ background: '#FDF9F2', borderBottom: '2px solid #e9ecef' }}>
                          <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600' }}>Date</th>
                          <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600' }}>Amount</th>
                          <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600' }}>Status</th>
                          <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600' }}>Method</th>
                          <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600' }}>Billing Period</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentHistory
                          .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                          .map((payment, index) => (
                          <tr key={index} style={{ borderBottom: '1px solid #e9ecef' }}>
                            <td style={{ padding: '15px' }}>{formatDate(payment.paid_date)}</td>
                            <td style={{ padding: '15px', fontWeight: '600', color: '#1E5B3A' }}>₹{payment.amount}</td>
                            <td style={{ padding: '15px' }}>
                              <span style={{
                                background: payment.status === 'paid' ? '#2E7D4F' : '#ff8080',
                                color: 'white',
                                padding: '5px 12px',
                                borderRadius: '12px',
                                fontSize: '0.85em',
                                fontWeight: '600',
                                textTransform: 'uppercase'
                              }}>
                                {payment.status}
                              </span>
                            </td>
                            <td style={{ padding: '15px', textTransform: 'capitalize' }}>
                              {payment.method || 'N/A'}
                            </td>
                            <td style={{ padding: '15px', fontSize: '0.85em', color: '#666' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div>
                                  <span style={{ fontSize: '0.85em', color: '#999', fontWeight: '600' }}>From:</span>
                                  <br />
                                  <span>{formatDateTime(payment.billing_start)}</span>
                                </div>
                                <div>
                                  <span style={{ fontSize: '0.85em', color: '#999', fontWeight: '600' }}>To:</span>
                                  <br />
                                  <span>{formatDateTime(payment.billing_end)}</span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination Controls */}
                  {paymentHistory.length > itemsPerPage && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '10px',
                      marginTop: '30px',
                      paddingTop: '20px',
                      borderTop: '1px solid #e9ecef'
                    }}>
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        style={{
                          padding: '10px 20px',
                          borderRadius: '8px',
                          border: '1px solid #1E5B3A',
                          background: currentPage === 1 ? '#f0f0f0' : 'white',
                          color: currentPage === 1 ? '#999' : '#1E5B3A',
                          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                          fontWeight: '600',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <i className="fa-solid fa-chevron-left"></i> Previous
                      </button>
                      
                      <span style={{ 
                        fontSize: '1em', 
                        color: '#666',
                        fontWeight: '600'
                      }}>
                        Page {currentPage} of {Math.ceil(paymentHistory.length / itemsPerPage)}
                      </span>
                      
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(paymentHistory.length / itemsPerPage)))}
                        disabled={currentPage === Math.ceil(paymentHistory.length / itemsPerPage)}
                        style={{
                          padding: '10px 20px',
                          borderRadius: '8px',
                          border: '1px solid #1E5B3A',
                          background: currentPage === Math.ceil(paymentHistory.length / itemsPerPage) ? '#f0f0f0' : 'white',
                          color: currentPage === Math.ceil(paymentHistory.length / itemsPerPage) ? '#999' : '#1E5B3A',
                          cursor: currentPage === Math.ceil(paymentHistory.length / itemsPerPage) ? 'not-allowed' : 'pointer',
                          fontWeight: '600',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        Next <i className="fa-solid fa-chevron-right"></i>
                      </button>
                    </div>
                  )}
                </div>
                )}

              </div>
            </div>
          </div>
        </section>

        {/* Validation Modal */}
        {showValidationModal && validationResult && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
            }}>
              {/* Header */}
              <div style={{
                background: validationResult.scenario === 'upgrade' 
                  ? 'linear-gradient(135deg, #2E7D4F 0%, #2E7D4F 100%)'
                  : validationResult.scenario === 'downgrade'
                  ? 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)'
                  : 'linear-gradient(135deg, #1E5B3A 0%, #2E7D4F 100%)',
                padding: '20px',
                borderRadius: '16px 16px 0 0'
              }}>
                <h3 style={{ color: 'white', margin: 0, fontSize: '1.5em', fontWeight: '700' }}>
                  {validationResult.scenario === 'upgrade' ? '⬆️ Subscription Upgrade' : 
                   validationResult.scenario === 'downgrade' ? '⬇️ Subscription Downgrade' : 
                   '🔄 Change Subscription'}
                </h3>
              </div>

              {/* Body */}
              <div style={{ padding: '30px' }}>
                {/* Message */}
                <p style={{ fontSize: '1.1em', color: '#333', marginBottom: '20px', fontWeight: '600' }}>
                  {validationResult.message}
                </p>

                {/* Warnings */}
                {validationResult.warnings && validationResult.warnings.length > 0 && (
                  <div style={{
                    background: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '8px',
                    padding: '15px',
                    marginBottom: '20px'
                  }}>
                    <h4 style={{ color: '#856404', margin: '0 0 10px 0', fontSize: '1.1em' }}>
                      ⚠️ Important Information:
                    </h4>
                    {validationResult.warnings.map((warning, index) => (
                      <p key={index} style={{ color: '#856404', margin: '5px 0', fontSize: '0.95em' }}>
                        {warning}
                      </p>
                    ))}
                  </div>
                )}

                {/* Gains */}
                {validationResult.gains && validationResult.gains.length > 0 && (
                  <div style={{
                    background: '#d4edda',
                    border: '1px solid #2E7D4F',
                    borderRadius: '8px',
                    padding: '15px',
                    marginBottom: '20px'
                  }}>
                    <h4 style={{ color: '#155724', margin: '0 0 10px 0', fontSize: '1.1em' }}>
                      ✅ What You'll Gain:
                    </h4>
                    {validationResult.gains.map((gain, index) => (
                      <p key={index} style={{ color: '#155724', margin: '5px 0', fontSize: '0.95em' }}>
                        {gain}
                      </p>
                    ))}
                  </div>
                )}

                {/* Losses */}
                {validationResult.losses && validationResult.losses.length > 0 && (
                  <div style={{
                    background: '#fff4f0',
                    border: '1px solid #ffb3b3',
                    borderRadius: '8px',
                    padding: '15px',
                    marginBottom: '20px'
                  }}>
                    <h4 style={{ color: '#721c24', margin: '0 0 10px 0', fontSize: '1.1em' }}>
                      ❌ What You'll Lose:
                    </h4>
                    {validationResult.losses.map((loss, index) => (
                      <p key={index} style={{ color: '#721c24', margin: '5px 0', fontSize: '0.95em' }}>
                        {loss}
                      </p>
                    ))}
                  </div>
                )}

                {/* Days Lost Warning */}
                {validationResult.daysLost > 0 && (
                  <div style={{
                    background: '#fff3cd',
                    border: '2px solid #ffc107',
                    borderRadius: '8px',
                    padding: '15px',
                    marginBottom: '20px',
                    textAlign: 'center'
                  }}>
                    <p style={{ color: '#856404', margin: 0, fontSize: '1.1em', fontWeight: '700' }}>
                       You will lose {validationResult.daysLost} days (NO REFUND)
                    </p>
                  </div>
                )}

                {/* Buttons */}
                <div style={{ display: 'flex', gap: '15px', marginTop: '25px', flexDirection: 'column' }}>
                  <button
                    onClick={async () => {
                      setShowValidationModal(false);
                      setLoading(true);
                      
                      try {
                        // First, cancel the current subscription
                        toast.info('Cancelling current subscription...');
                        
                        const cancelResponse = await api.post('/subscription/cancel', {
                          business_user_id: businessUserId,
                          subscription_id: subscriptionInfo.subscription_id
                        });
                        
                        if (cancelResponse.data.status === 'success') {
                          toast.success('Current subscription cancelled!');
                          
                          // Wait a moment for cancellation to process
                          await new Promise(resolve => setTimeout(resolve, 1000));
                          
                          // Now proceed with new subscription
                          toast.info('Creating new subscription...');
                          await proceedWithSubscription(pendingPlanId, pendingDiscountId);
                        } else {
                          throw new Error('Failed to cancel current subscription');
                        }
                      } catch (error) {
                        console.error('Error during subscription change:', error);
                        toast.error('Failed to change subscription. Please try again.');
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                    style={{
                      padding: '15px',
                      fontSize: '1.1em',
                      fontWeight: '600',
                      color: 'white',
                      background: validationResult.scenario === 'downgrade' 
                        ? 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)' 
                        : 'linear-gradient(135deg, #2E7D4F 0%, #2E7D4F 100%)',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: loading ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.currentTarget.style.opacity = '0.9';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading) {
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    {loading ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin"></i>
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-sync-alt"></i>
                        Cancel Current & Subscribe to {pendingPlanName}
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowValidationModal(false);
                      setPendingPlanId(null);
                      setPendingPlanName(null);
                      setPendingDiscountId(null);
                      setValidationResult(null);
                    }}
                    disabled={loading}
                    style={{
                      padding: '15px',
                      fontSize: '1.1em',
                      fontWeight: '600',
                      color: '#333',
                      background: '#e0e0e0',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: loading ? 0.6 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.currentTarget.style.background = '#d0d0d0';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading) {
                        e.currentTarget.style.background = '#e0e0e0';
                      }
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      </>
    );
  }
};

// Helper component for plan cards (WITH DISCOUNT SUPPORT)
const PlanCard = ({ 
  planId, 
  name, 
  price, 
  originalPrice,
  description, 
  features, 
  isPopular, 
  isCurrent, 
  onSubscribe, 
  loading, 
  businessUserId, 
  isPaid, 
  billingPeriod,
  hasDiscount,
  discount,
  subscriptionInfo,
  yearlyPrice
}) => {
  // Decided by the page from /subscription/info (see utils/currentPlan.js)
  const isCurrentPlan = !!isCurrent;
  // Only show as active/paid if it's the current plan AND subscription is paid/active
  const isActivePaidPlan = isCurrentPlan && isPaid;
  
  // Check if this plan was purchased with a discount
  const wasPurchasedWithDiscount = isActivePaidPlan && 
    subscriptionInfo && 
    subscriptionInfo.first_payment_discount_amount > 0;
  
  // Format billing period for display
  const billingText = billingPeriod === 'yearly' ? '/Year' : billingPeriod === 'quarterly' ? '/Quarter' : '/Month';
  
  // Calculate display price (use discount final_price if available, otherwise regular price)
  const displayPrice = hasDiscount && discount ? discount.final_price : (originalPrice || price);
  
  // Crossed-out price: 12 months at one tenth of the yearly price
  const wasPrice = isYearlyPlan({ billing_period: billingPeriod }) ? crossedOutPrice(yearlyPrice) : null;

  return (
    <article className={`pc${isActivePaidPlan || hasDiscount || isPopular ? ' pop' : ''}`}>
      {/* PAID tag on the current paid plan, else Most popular on the middle plan */}
      {isActivePaidPlan ? (
        <span className="tagpop paid">✓ Paid</span>
      ) : isPopular && !hasDiscount && (
        <span className="tagpop">★ Most popular</span>
      )}

      <h2>{name}</h2>
      {description && <p className="pp-desc">{description}</p>}

      {/* Price Section with Discount Support */}
      {hasDiscount && discount ? (
        <>
          <span className="pp-deal">{discount.discount_name}</span>
          <div className="pr2">
            <b>₹{typeof displayPrice === 'number' ? displayPrice.toLocaleString('en-IN') : displayPrice}</b>
            <span>{billingText}</span>
            <s>₹{originalPrice || price}</s>
          </div>
          <div className="meta">
            <em>
              {discount.discount_type === 'percentage'
                ? `${discount.discount_value}% OFF`
                : `₹${discount.discount_amount} OFF`}
            </em>
            <span>Save ₹{discount.discount_amount}</span>
            <CountdownTimer endDate={discount.end_date} />
          </div>
          <p className="pp-renew">Renews at ₹{originalPrice || price}{billingText}</p>
        </>
      ) : (
        <div className="pr2">
          <b>₹{price}</b>
          <span>{billingText}</span>
          {wasPrice !== null && <s>₹{wasPrice.toLocaleString('en-IN')}</s>}
        </div>
      )}

      {features.length > 0 && (
        <ul className="pp-feats">
          {features.map((feature, index) => (
            <li key={index}>{feature}</li>
          ))}
        </ul>
      )}

      {isActivePaidPlan ? (
        <div className="pp-cta">
          <button type="button" className="pp-btn cur" disabled>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m5 12 5 5 9-10" /></svg>
            Active Plan
          </button>

          {/* Purchase Info - Show for active paid plans */}
          {wasPurchasedWithDiscount && subscriptionInfo && (
            <div className="pp-paidnote">
              <strong>Purchased with discount</strong>
              <span>You saved ₹{subscriptionInfo.first_payment_discount_amount}</span>
              <span>
                Next billing: {subscriptionInfo.next_billing_date ? new Date(subscriptionInfo.next_billing_date).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'} at ₹{originalPrice || price}
              </span>
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          className={`pp-btn ${isPopular || hasDiscount ? 'o' : 'gh'}`}
          onClick={() => onSubscribe(planId, name, hasDiscount && discount ? discount.discount_id : null)}
          disabled={loading || !businessUserId}
        >
          {loading ? 'Loading...' : hasDiscount ? 'Get Discount Now' : 'Subscribe Now'}
        </button>
      )}
    </article>
  );
};

// Helper component for feature rows
const FeatureRow = ({ feature, basic, premium, enterprise }) => {
  const renderValue = (value) => {
    if (value === '✓') return <span style={{ color: '#2E7D4F', fontSize: '1.3em', fontWeight: '700' }}>✓</span>;
    if (value === '✕') return <span style={{ color: '#ff8080', fontSize: '1.3em', fontWeight: '700' }}>✕</span>;
    return <span style={{ color: '#666', fontSize: '0.9em' }}>{value}</span>;
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', padding: '18px 20px', borderBottom: '1px solid #e9ecef', alignItems: 'center' }}>
      <div style={{ color: '#333', fontSize: '0.95em', paddingRight: '20px', fontStyle: "italic" }}>{feature}</div>
      <div style={{ textAlign: 'center' }}>{renderValue(basic)}</div>
      <div style={{ textAlign: 'center' }}>{renderValue(premium)}</div>
      <div style={{ textAlign: 'center' }}>{renderValue(enterprise)}</div>
    </div>
  );
};

// Add CSS animations for discount effects
const style = document.createElement('style');
style.textContent = `
    @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.9; }
    }
    
    @keyframes pulse-border {
        0%, 100% {
            border-color: #1E5B3A;
            box-shadow: 0 0 0 0 rgba(124, 58, 237, 0.4);
        }
        50% {
            border-color: #a78bfa;
            box-shadow: 0 0 0 8px rgba(124, 58, 237, 0);
        }
    }
        50% {
            border-color: #ff7875;
            box-shadow: 0 0 0 8px rgba(255, 77, 79, 0);
        }
    }
`;
if (!document.getElementById('subscription-discount-animations')) {
    style.id = 'subscription-discount-animations';
    document.head.appendChild(style);
}

export default Subscription;
